import React, { useCallback, useContext, useEffect, useState } from "react";
import { fetchQuery } from "react-relay";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import { motion } from "framer-motion";

import { environment } from "~/models";
import { Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import type { Event, MeetingCostSettings, MeetingGuidelinesSettings } from "./CalendarPage";
import EventForm, { eventFormDetails, type EventFormResult } from "./EventForm/EventForm";
import { EventFormContext } from "./EventForm/EventFormContext";
import useDeleteEvent from "./hooks/useDeleteEvent";
import { SHARP_EASE_OUT_CUBIC_BEZIER, SidePanelNavContext } from "./SidePanel";

import type { EventForm_viewer$key } from "~/__generated__/EventForm_viewer.graphql";
import type { EventFormDetailQuery } from "~/__generated__/EventFormDetailQuery.graphql";

import styles from "./EventFormPanel.module.css";
import panelStyles from "./SidePanel.module.css";

interface EventFormPanelProps {
	closePanel: () => void;
	checkAndClose: () => void;
	onFormFinish: (result: EventFormResult) => void;
	panelWasOpen: boolean;
	viewer: EventForm_viewer$key;
	eventToEdit?: Event;
	meetingGuidelinesSettings: MeetingGuidelinesSettings;
	meetingCostSettings: MeetingCostSettings;
	hasAccessToSchedulingMeetingAssistant: boolean;
}

export default function EventFormPanel({
	checkAndClose,
	onFormFinish,
	panelWasOpen,
	viewer,
	eventToEdit,
	meetingGuidelinesSettings,
	meetingCostSettings,
	hasAccessToSchedulingMeetingAssistant,
}: EventFormPanelProps) {
	const [eventAdditional, setEventAdditional] = useState<EventFormDetailQuery["response"]["event"]>(null);
	const [additionalLoading, setAdditionalLoading] = useState(false);
	const { setEventTitle, changeTime } = useContext(EventFormContext);

	const { setFormHasEdits } = useContext(SidePanelNavContext);
	const setHasEdits = useCallback(() => {
		setFormHasEdits(true);
	}, [setFormHasEdits]);

	const deleteEvent = useDeleteEvent(eventToEdit);
	const onDeleteEvent = useCallback(async () => {
		const deletedEventGuid = await deleteEvent();
		if (deletedEventGuid) {
			onFormFinish({ type: "deleted", event: { eventGuid: deletedEventGuid } });
		}
	}, [deleteEvent, onFormFinish]);

	useEffect(() => {
		setAdditionalLoading(!!eventToEdit?.guid);
		setEventAdditional(null);

		if (!eventToEdit?.guid) return;

		fetchQuery<EventFormDetailQuery>(environment, eventFormDetails, { guid: eventToEdit.guid })
			.toPromise()
			.then(response => {
				setAdditionalLoading(false);

				if (response) {
					setEventAdditional(response.event);
				}
			});
	}, [eventToEdit?.guid]);

	useEffect(() => {
		if (eventToEdit) {
			setEventTitle(eventToEdit.title);
			changeTime({
				type: "SET",
				newStart: eventToEdit.start,
				newEnd: eventToEdit.end,
				isAllDay: eventToEdit.isAllDay,
			});
		}
	}, [eventToEdit, setEventTitle, changeTime]);

	return (
		<motion.div
			className={styles.levelTwoPanel}
			key="event-form"
			layout
			initial={panelWasOpen ? { left: "100%" } : false}
			transition={{ duration: 0.3, ease: SHARP_EASE_OUT_CUBIC_BEZIER }}
			animate={{ left: "0%" }}
			exit={{ left: "100%" }}
		>
			<header className={cx(panelStyles.sidePanel__header, styles.formHeader)}>
				<div className={panelStyles.sidePanel__header__title}>{eventToEdit ? "Edit event" : "Create event"}</div>

				<div className={panelStyles.buttons}>
					{eventToEdit && (
						<Tooltip content="Delete event" placement="bottom">
							<IconButton title="Delete" icon={faTrashCan} onClick={onDeleteEvent} />
						</Tooltip>
					)}
					<IconButton title="Close" icon={faTimes} onClick={checkAndClose} />
				</div>
			</header>

			{eventToEdit && (eventAdditional === null || additionalLoading) ? (
				<div className={styles.formContent}>
					<LoadingSpinner dimmer={false} delay={200} />
				</div>
			) : (
				<EventForm
					key={eventAdditional?.guid}
					viewer={viewer}
					meetingGuidelinesSettings={meetingGuidelinesSettings}
					meetingCostSettings={meetingCostSettings}
					eventAdditional={eventAdditional}
					setHasEdits={setHasEdits}
					onFormFinish={onFormFinish}
					hasAccessToSchedulingMeetingAssistant={hasAccessToSchedulingMeetingAssistant}
				/>
			)}
		</motion.div>
	);
}
